import { Loader2 } from "lucide-react";

export const inputClass = (isDark) =>
  `w-full text-[13px] px-3 py-2 rounded-md border outline-none transition-colors ${
    isDark
      ? "bg-white/[0.03] border-white/[0.08] text-gray-200 placeholder:text-gray-600 focus:border-[#4d7ee0]"
      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#1E4FCF]"
  }`;

export const buttonClass = (isDark, variant) => {
  const base =
    "inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  if (variant === "primary") {
    return `${base} ${
      isDark
        ? "bg-[#1E4FCF] text-white hover:bg-[#2a5cdc]"
        : "bg-[#1E4FCF] text-white hover:bg-[#1844b8]"
    }`;
  }
  if (variant === "secondary") {
    return `${base} ${
      isDark
        ? "bg-white/[0.06] text-gray-200 hover:bg-white/[0.1]"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`;
  }
  if (variant === "danger") {
    return `${base} ${
      isDark
        ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
        : "bg-red-50 text-red-600 hover:bg-red-100"
    }`;
  }
  return `${base} ${
    isDark
      ? "text-gray-400 hover:text-gray-200"
      : "text-gray-500 hover:text-gray-800"
  }`;
};

export const FormField = ({ label, hint, isDark, children }) => (
  <div className="flex flex-col gap-1.5">
    <label
      className={`text-[12px] font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`}
    >
      {label}
      {hint && (
        <span
          className={`ml-2 font-normal ${
            isDark ? "text-gray-600" : "text-gray-400"
          }`}
        >
          {hint}
        </span>
      )}
    </label>
    {children}
  </div>
);

export const ColorPicker = ({ value, onChange, isDark }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-9 w-10 rounded-md cursor-pointer border ${
        isDark ? "border-white/[0.08]" : "border-gray-200"
      }`}
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass(isDark)}
    />
  </div>
);

export const SectionLoader = ({ isDark }) => (
  <div className="flex items-center justify-center py-16">
    <Loader2
      size={20}
      className={`animate-spin ${isDark ? "text-gray-500" : "text-gray-400"}`}
    />
  </div>
);

export const EmptyState = ({ isDark, title, description, action }) => (
  <div className="text-center py-12">
    <h3
      className={`text-[14px] font-semibold mb-1 ${
        isDark ? "text-gray-200" : "text-gray-800"
      }`}
    >
      {title}
    </h3>
    <p
      className={`text-[12px] max-w-md mx-auto ${
        isDark ? "text-gray-500" : "text-gray-500"
      }`}
    >
      {description}
    </p>
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);

export const Modal = ({ isOpen, onClose, title, isDark, children, maxWidth = "max-w-lg" }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full ${maxWidth} rounded-xl border shadow-xl ${
          isDark
            ? "bg-[#0f0f0f] border-white/[0.08]"
            : "bg-white border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-center justify-between px-5 py-4 border-b ${
            isDark ? "border-white/[0.06]" : "border-gray-100"
          }`}
        >
          <h2
            className={`text-[14px] font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className={`text-[18px] leading-none ${
              isDark
                ? "text-gray-500 hover:text-gray-200"
                : "text-gray-400 hover:text-gray-700"
            }`}
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

export const isNotFoundError = (e) => e?.response?.status === 404;
